import { Component, ElementRef, ViewChild, OnDestroy, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { SidebarService } from '../../shared/services/sidebar.service';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { UserDropdownComponent } from '../../shared/components/header/user-dropdown/user-dropdown.component';
import { ThemeService } from '../../shared/services/theme.service';
import { KeyboardShortcutService, ShortcutCommand } from '../../shared/services/keyboard-shortcut.service';
import { AuthService } from '../../../application/services/auth.service';
import { NgIconComponent } from '@ng-icons/core';
import { toNgIconName } from '../../shared/utils/heroicons';

interface SearchItem {
  group: string;
  name: string;
  path: string;
  icon: string; // ng-icon name, e.g. "heroTruck"
}

// Header de la aplicacion. Incluye el boton para colapsar el sidebar,
// el toggle de tema oscuro/claro, el dropdown del usuario y la paleta
// de comandos que se puede abrir con Ctrl+K y arrastrar por la pantalla.
@Component({
  selector: 'app-header',
  imports: [
    CommonModule,
    RouterModule,
    UserDropdownComponent,
    NgIconComponent,
  ],
  templateUrl: './app-header.component.html',
})
export class AppHeaderComponent implements OnDestroy {
  isApplicationMenuOpen = false;
  showCommandPalette = false;
  commands: ShortcutCommand[] = [];
  readonly isMobileOpen$;
  private escSub: Subscription;

  // Variables para el arrastre de la paleta de comandos
  isDragging = false;
  dragX = 0;
  dragY = 0;
  private startX = 0;
  private startY = 0;
  private onMouseMoveBound = this.onMouseMove.bind(this);
  private onMouseUpBound = this.onMouseUp.bind(this);

  // Búsqueda en el header
  searchQuery = '';
  showSearchDropdown = false;
  filteredSearchItems: SearchItem[] = [];
  private allSearchItems: SearchItem[] = [];

  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;

  private router = inject(Router);
  private authService = inject(AuthService);

  constructor(
    public sidebarService: SidebarService,
    public themeService: ThemeService,
    private shortcutService: KeyboardShortcutService
  ) {
    this.isMobileOpen$ = this.sidebarService.isMobileOpen$;
    this.commands = this.shortcutService.getCommands();
    // Cierra la paleta si el servicio de shortcuts emite un ESC
    this.escSub = this.shortcutService.closeModal$.subscribe(() => {
      if (this.showCommandPalette) this.closeCommandPalette();
    });
    this.buildSearchItems();
  }

  private buildSearchItems(): void {
    const modules = this.authService.getModules();
    for (const mod of modules) {
      for (const screen of mod.screens) {
        this.allSearchItems.push({
          group: mod.name,
          name: screen.name,
          path: screen.path,
          icon: toNgIconName(mod.icon),
        });
      }
    }
  }

  onSearch(value: string): void {
    this.searchQuery = value;
    const q = value.trim().toLowerCase();
    if (!q) {
      this.showSearchDropdown = false;
      this.filteredSearchItems = [];
      return;
    }
    this.filteredSearchItems = this.allSearchItems.filter(
      item =>
        item.name.toLowerCase().includes(q) ||
        item.group.toLowerCase().includes(q),
    );
    this.showSearchDropdown = true;
  }

  selectFirstSearchItem(): void {
    if (this.filteredSearchItems.length > 0) {
      this.selectSearchItem(this.filteredSearchItems[0].path);
    }
  }

  selectSearchItem(path: string): void {
    this.router.navigate([path]);
    this.searchQuery = '';
    this.showSearchDropdown = false;
    this.filteredSearchItems = [];
    if (this.searchInput) this.searchInput.nativeElement.value = '';
  }

  closeSearchDropdown(): void {
    // Delay so click on item fires first
    setTimeout(() => {
      this.showSearchDropdown = false;
    }, 150);
  }

  // En pantallas grandes colapsa/expande el sidebar; en movil lo abre/cierra
  handleToggle() {
    if (window.innerWidth >= 1280) {
      this.sidebarService.toggleExpanded();
    } else {
      this.sidebarService.toggleMobileOpen();
    }
  }

  toggleApplicationMenu() {
    this.isApplicationMenuOpen = !this.isApplicationMenuOpen;
  }

  toggleTheme() {
    this.themeService.toggleTheme();
  }

  // Abre o cierra la paleta de comandos y la resetea a su posicion inicial
  toggleCommandPalette() {
    this.showCommandPalette = !this.showCommandPalette;
    if (this.showCommandPalette) {
      this.dragX = 0;
      this.dragY = 0;
    }
  }

  closeCommandPalette() {
    this.showCommandPalette = false;
  }

  // Inicia el arrastre de la paleta cuando el usuario hace click sobre su header
  onPaletteDragStart(event: MouseEvent): void {
    this.isDragging = true;
    this.startX = event.clientX - this.dragX;
    this.startY = event.clientY - this.dragY;
    document.addEventListener('mousemove', this.onMouseMoveBound);
    document.addEventListener('mouseup', this.onMouseUpBound);
  }

  private onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    this.dragX = event.clientX - this.startX;
    this.dragY = event.clientY - this.startY;
  }

  private onMouseUp(): void {
    this.isDragging = false;
    document.removeEventListener('mousemove', this.onMouseMoveBound);
    document.removeEventListener('mouseup', this.onMouseUpBound);
  }

  ngAfterViewInit() {
    document.addEventListener('keydown', this.handleKeyDown);
  }

  ngOnDestroy() {
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('mousemove', this.onMouseMoveBound);
    document.removeEventListener('mouseup', this.onMouseUpBound);
    this.escSub.unsubscribe();
  }

  // Ctrl+K o Cmd+K mueve el foco al campo de busqueda de la paleta
  handleKeyDown = (event: KeyboardEvent) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      this.searchInput?.nativeElement.focus();
    }
  };
}
